import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Input } from 'antd';

/**
 * Drop-in replacement for antd <Input> for fields wired straight into the form
 * builder's `updateSchema` (which deep-clones the whole schema + re-renders the
 * builder and live preview on every change). Firing that on every keystroke is
 * what makes typing in question/section labels feel choppy.
 *
 * Keeps a local echo for instant, jank-free typing and only calls the real
 * onChange (an `{ target: { value } }`-shaped event, matching plain <Input>)
 * after a short pause or on blur.
 */
const DebouncedInput = ({ value, onChange, onBlur, onFocus, delay = 300, ...rest }) => {
  const [localValue, setLocalValue] = useState(value);
  const timerRef = useRef(null);
  const isFocusedRef = useRef(false);

  useEffect(() => {
    if (!isFocusedRef.current) setLocalValue(value);
  }, [value]);

  useEffect(() => () => clearTimeout(timerRef.current), []);

  const commit = useCallback((val) => {
    clearTimeout(timerRef.current);
    onChange?.({ target: { value: val } });
  }, [onChange]);

  const handleChange = (e) => {
    const val = e.target.value;
    setLocalValue(val);
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => commit(val), delay);
  };

  return (
    <Input
      {...rest}
      value={localValue}
      onFocus={(e) => { isFocusedRef.current = true; onFocus?.(e); }}
      onChange={handleChange}
      onBlur={(e) => { isFocusedRef.current = false; commit(e.target.value); onBlur?.(e); }}
    />
  );
};

export default DebouncedInput;
